import { Box, Button, List, ListItem, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { BusinessUnitDetails, PaymentSolutionNameRequest, getBusinessUnitDetails } from '../../Api/BoardingApi';
import { useLanguage } from '../../LanguageContext';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import Loader from '../Loader/Loader';
import PageContent from '../PageContent/PageContent';
import Sidebar from '../Sidebar/Sidebar';

const Confirmation: any = () => {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [businessUnitDetails, setBusinessUnitDetails] = useState<BusinessUnitDetails>({
        businessUnitName: "",
        fullsteamEmail: "",
        logoArray: "",
        languageCultures: []
    });
    const { paymentSolutionName } = useParams<{ paymentSolutionName: string }>();
    const { culture } = useParams<{ culture: string }>();
    const navigate = useNavigate();

    //Handle translation
    const { changeLanguage } = useLanguage();
    const { t } = useTranslation();
    useEffect(() => {
        if (culture) {
            changeLanguage(culture);
        }
    }, [culture]);

    const finishLoading = (elapsedMilliseconds: number) => {
        let waitTime = normalizedWaitMilliseconds(elapsedMilliseconds);
        setTimeout(() => { setIsLoading((isLoading: boolean) => !isLoading) }, waitTime);
    }

    const normalizedWaitMilliseconds = (elapsedMilliseconds: number) => {
        if (elapsedMilliseconds < 0)
            return 0;

        let bufferMilliseconds = 300;
        let normalizedWaitMilliseconds = bufferMilliseconds - elapsedMilliseconds;
        return Math.max(normalizedWaitMilliseconds, 0);
    }

    useEffect(() => {
        let isMounted = true;

        const fetchBusinessUnitDetails = async () => {
            if (!paymentSolutionName) return;

            const startTime = Date.now();

            const paymentSolutionNameRequest: PaymentSolutionNameRequest = {
                paymentSolutionName
            };

            try {
                const response: AxiosResponse<BusinessUnitDetails> = await getBusinessUnitDetails(paymentSolutionNameRequest);
                if (isMounted) {
                    setBusinessUnitDetails(response.data);
                    if (!culture || !response.data.languageCultures.some(lc => lc.culture === culture)) {
                        navigate(`/${paymentSolutionName}`);
                    }
                }
            } catch (error) {
                console.error('Error fetching business unit details:', error);
                if (isMounted) {
                    navigate('/error');
                }
            } finally {
                if (isMounted) {
                    const elapsedMilliseconds = Date.now() - startTime;
                    finishLoading(elapsedMilliseconds);
                }
            }
        };

        fetchBusinessUnitDetails();

        return () => {
            isMounted = false;
        };
    }, [paymentSolutionName, culture, navigate]);

    const closeTab = () => {
        const newWindow = window.open('about:blank', '_self');
        newWindow?.close();
    };

    document.title = t('Document_title').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const heading = t('Confirmation_heading').replace('{{businessUnitName}}', businessUnitDetails?.businessUnitName);
    const sideBarHeading: string = `${t('Confirmation_sideBarHeading')}:`;
    const sideBarBullet1 = <>{t('Confirmation_sideBarBullet1')} <a id="fullsteam-1-email-link" href={`mailto: ${businessUnitDetails?.fullsteamEmail}`}>{businessUnitDetails?.fullsteamEmail}</a>.</>;
    const sideBarBullet2: string = t('Confirmation_sideBarBullet2');
    const sideBarBullet3 = <>{t('Confirmation_sideBarBullet3')} <a id="fullsteam-2-email-link" href={`mailto: ${businessUnitDetails?.fullsteamEmail}`}>{businessUnitDetails?.fullsteamEmail}</a>.</>;
    const sideBarText: string = t('Confirmation_sideBarText');
    const pageContentHeading: string = t('Confirmation_pageContentHeading');
    const pageContentText: string = t('Confirmation_pageContentText');
    const pageContentBullet1: string = t('Confirmation_pageContentBullet1');
    const pageContentBullet2: string = t('Confirmation_pageContentBullet2');
    const pageContentBullet3: string = t('Confirmation_pageContentBullet3');
    const pageContentBullet4: string = t('Confirmation_pageContentBullet4');
    const pageContentBullet5: string = t('Confirmation_pageContentBullet5');
    const pageContentBullet6: string = t('Confirmation_pageContentBullet6');
    const pageContentBullet7: string = t('Confirmation_pageContentBullet7');
    const pageContentBullet8: string = t('Confirmation_pageContentBullet8');
    const closeButtonText: string = t('Confirmation_closeButtonText');

    return (

        <> {isLoading ?
            <Loader /> :
            <Box id="confirmation">
                <Box className="banner" />
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>

                    <Grid size={{ xs: 4, sm: 8, md: 4 }}>
                        <Sidebar title={sideBarHeading} logo={businessUnitDetails?.logoArray} alt={businessUnitDetails?.businessUnitName}>
                            <List className="reset">
                                <ListItem className="count" data-test-id="next-steps-1"><Typography variant="body2" component="span" gutterBottom>{sideBarBullet1}</Typography></ListItem>
                                <ListItem className="count" data-test-id="next-steps-2"><Typography variant="body2" component="span" gutterBottom>{sideBarBullet2}</Typography></ListItem>
                                <ListItem className="count" data-test-id="next-steps-3"><Typography variant="body2" component="span" gutterBottom>{sideBarBullet3}</Typography></ListItem>
                            </List>
                            <Typography variant="body2" component="p" data-test-id="next-steps-bottom-text" gutterBottom>{sideBarText}</Typography>
                        </Sidebar>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 8, md: 8 }}>
                        <Header><span id="confirmation-business-unit-heading-text">{heading}</span></Header>
                        <PageContent title={pageContentHeading}>
                            <Typography variant="body1" component="p" data-test-id="tips-to-expedite-top-text" gutterBottom>{pageContentText}</Typography>
                            <List>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-1" gutterBottom>&#8226; {pageContentBullet1}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-2" gutterBottom>&#8226; {pageContentBullet2}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-3" gutterBottom>&#8226; {pageContentBullet3}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-4" gutterBottom>&#8226; {pageContentBullet4}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-5" gutterBottom>&#8226; {pageContentBullet5}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-6" gutterBottom>&#8226; {pageContentBullet6}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-7" gutterBottom>&#8226; {pageContentBullet7}</Typography></ListItem>
                                <ListItem><Typography variant="body1" component="span" data-test-id="tips-to-expedite-bullet-8" gutterBottom>&#8226; {pageContentBullet8}</Typography></ListItem>
                            </List>
                            <hr />
                            <Box className="center-container">
                                <Box className="center-item">
                                    <Button onClick={closeTab} variant="contained" id="closeButton" data-test-id="close-page-button" className="fullsteam-button">{closeButtonText}</Button>
                                </Box>
                            </Box>
                        </PageContent>
                        <Footer culture={culture} />
                    </Grid>

                </Grid>
            </Box>
        }
        </>
    );
}

export default Confirmation;
